import type { FieldInstanceFields } from "@regrello/graphql-api";
import {
  type RegrelloDataGridV2EditableCellProps,
  useDataGridV2EditableCell,
  useDataGridV2FirstKeystroke,
} from "@regrello/ui-core";

import { RegrelloFormFieldNumber } from "../../../../formFields/RegrelloFormFieldNumber";
import type { CurrencyFieldPluginFrontendValue } from "./types";

export interface RegrelloEditableCellCurrencyProps {
  /** The field instance that corresponds to the current table column. */
  fieldInstance: FieldInstanceFields;

  /**
   * The current value to display in the cell. This is the value before editing and the value that
   * will be reinstated if editing is canceled.
   */
  value: CurrencyFieldPluginFrontendValue;

  /** The callback to invoke when the user changes the value of the cell. */
  onValueChange: (value: CurrencyFieldPluginFrontendValue) => void;
}

/**
 * An editable cell in which the user can input a currency value. Intended for use in
 * `RegrelloDataGridV2`.
 */
export function RegrelloEditableCellCurrency({
  api,
  eventKey,
  fieldInstance,
  value,
  onValueChange,
}: RegrelloDataGridV2EditableCellProps<RegrelloEditableCellCurrencyProps>) {
  if (fieldInstance.spectrumFieldVersion == null) {
    throw new Error("spectrumFieldVersioIn is unexpectedly not defined");
  }

  const { columnWidth, onKeyDownCapture } = useDataGridV2EditableCell({ api, eventKey });

  const { inputRef } = useDataGridV2FirstKeystroke({
    eventKey,
    onValueChange,
    parseEventKey: (ek) => Number.parseFloat(ek),
    value,
  });

  return (
    // Currency fields are full of content and are difficult to shrink.
    <div className="min-w-42" style={{ width: columnWidth }}>
      <RegrelloFormFieldNumber
        autoFocus={true}
        inputClassName="rounded-none"
        inputRef={inputRef}
        isDefaultMarginsOmitted={true}
        onBlur={() => {
          api.stopEditing();
        }}
        onKeyDownCapture={onKeyDownCapture}
        onValueChange={onValueChange}
        size="x-small"
        value={value}
      />
    </div>
  );
}
